/**
 * @author Fernando Ruano
 * @file index.js
 * @description AppBreadcrumb Component
 */

// Dependencies
import React from 'react';
import { Breadcrumb } from 'antd';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import stringSimilarity from 'string-similarity';
import { useTranslation } from 'react-i18next';

// Routes names
import {
  pageTitlesStaff,
  pageTitlesPatient
} from '@nephro/components/PageHeader/titles';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';

const { Item: BreadcrumbItem } = Breadcrumb;

/**
 * Calculates routes for breadcrumb.
 * @param { String } pathname - path name.
 * @return { Array } Array with routes.
 */
const calculateRoutes = (pathname, isPatient) => {
  let isEducationalMaterial = false;
  const intlBaseRouteId = '';
  const titlesByUserType = isPatient ? pageTitlesPatient('') : pageTitlesStaff;
  const pathnameSplit = pathname?.split('/');

  let categoryName = { intlId: '', path: '' };
  let elementName = { intlId: '', path: '' };
  if (pathnameSplit[2] === 'educational-material') {
    isEducationalMaterial = true;
    categoryName = { intlId: pathnameSplit[4], path: '' };
    if (pathnameSplit[3] === 'element') {
      elementName = { intlId: pathnameSplit[5], path: '' };
    }
  }

  const routes = pathnameSplit
    .filter((url) => url)
    .map((_, index, array) => {
      const currentUrls = array.slice(0, index + 1);
      const intlId = currentUrls.reduce(
        (start, path) => `${start}/${path}`,
        intlBaseRouteId
      );

      const urlName = titlesByUserType?.filter(
        (itm) =>
          intlId === itm.path ||
          stringSimilarity.compareTwoStrings(intlId, itm.path) > 0.9
      )[0];
      const path =
        index !== array.length - 1 ? `/${currentUrls.join('/')}` : null;
      return {
        path,
        intlId: urlName?.subTitle || urlName?.title
      };
    });
  if (isEducationalMaterial) {
    routes[2] = categoryName;
    routes[3] = elementName;
  }
  return routes;
};

/**
 * AppBreadCrumb Function component.
 * @param { Object } props - React PropTypes
 * @property { Object } location - React Router location Object.
 * @return { Node } React node containing app breadcrumb.
 */
export const AppBreadCrumb = ({ location }) => {
  const { pathname } = location;
  const [routes, setRoutes] = React.useState([]);
  const { t: translate } = useTranslation();

  const fixedRoutes = (array) => {
    const newRoutesArray = array;
    const arrayLen = newRoutesArray.length;
    if (
      (arrayLen > 1 &&
        newRoutesArray[arrayLen - 1]?.intlId ===
          newRoutesArray[arrayLen - 2]?.intlId) ||
      (arrayLen > 3 &&
        newRoutesArray[arrayLen - 2]?.intlId ===
          newRoutesArray[arrayLen - 3]?.intlId)
    ) {
      newRoutesArray.splice(arrayLen - 2, 1);
      fixedRoutes(newRoutesArray);
    }
    return newRoutesArray;
  };

  React.useEffect(() => {
    const onGetRoutes = async () => {
      const userType = await getStorageItem('isPatient');
      const routesArray = calculateRoutes(pathname, userType);
      setRoutes(fixedRoutes(routesArray));
    };
    onGetRoutes();
  }, [pathname, translate]);

  return (
    <Breadcrumb style={{ marginLeft: 7 }}>
      {routes.map(({ path, intlId }, key) => {
        const tagProps = path ? { href: `${path}` } : {};
        if (tagProps?.href === '/main/dashboard-detail') {
          delete tagProps?.href;
        }
        return (
          <BreadcrumbItem key={key} {...tagProps}>
            {path === '/main' ? 'Inicio' : translate(intlId)}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};

/**
 * @name AppBreadCrumb.propTypes
 * @type { propTypes }
 * @property { Object } location - React router location object.
 */
AppBreadCrumb.propTypes = {
  location: PropTypes.any
};

export default withRouter(AppBreadCrumb);
